/**
 * Transaction Excel Export Service
 * 
 * Handles exporting transactions to Excel format (.xlsx)
 */

import * as XLSX from 'xlsx';
import { supabase } from '../../../lib/supabase';
import type { CashFlowEntry } from '../../../types/database';

/**
 * Translate entry type to Portuguese
 */
function translateEntryType(type: string): string {
  const translations: Record<string, string> = {
    'income': 'Receita',
    'expense': 'Despesa',
    'transfer': 'Transferência',
  };
  return translations[type] || type;
}

/**
 * Translate payment status to Portuguese
 */
function translatePaymentStatus(status: string): string {
  const translations: Record<string, string> = {
    'paid': 'Pago',
    'pending': 'Pendente',
    'overdue': 'Vencido',
    'previsão': 'Previsão',
    'confirmado': 'Confirmado',
  };
  return translations[status] || status;
}

/**
 * Format date to Brazilian format
 */
function formatDate(dateString: string | null): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR');
}

/**
 * Format currency
 */
function formatCurrency(amount: number): string {
  return amount.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Translate project status to Portuguese
 */
function translateProjectStatus(status: string): string {
  const translations: Record<string, string> = {
    'active': 'Ativo',
    'completed': 'Concluído',
    'paused': 'Pausado',
  };
  return translations[status] || status;
}

/**
 * Fetch master data (categories, payment methods, etc)
 */
async function fetchMasterData(companyId: string | string[]) {
  const companyIds = Array.isArray(companyId) ? companyId : [companyId];

  const [categories, paymentMethods, businessPartners, subcategories, projects] = await Promise.all([
    supabase
      .from('categories')
      .select('*')
      .in('company_id', companyIds)
      .order('name'),
    supabase
      .from('payment_methods')
      .select('*')
      .in('company_id', companyIds)
      .order('name'),
    supabase
      .from('business_partners')
      .select('*')
      .in('company_id', companyIds)
      .order('name'),
    supabase
      .from('subcategories')
      .select('*')
      .in('company_id', companyIds)
      .order('main_category', { ascending: true })
      .order('name', { ascending: true }),
    supabase
      .from('projects')
      .select('*')
      .in('company_id', companyIds)
      .order('name'),
  ]);

  return {
    categories: categories.data || [],
    paymentMethods: paymentMethods.data || [],
    businessPartners: businessPartners.data || [],
    subcategories: subcategories.data || [],
    projects: projects.data || [],
  };
}

/**
 * Export transactions to Excel
 */
export async function exportTransactionsToExcel(
  transactions: CashFlowEntry[],
  companyId: string | string[],
  options: {
    includeMasterData?: boolean;
  } = {}
): Promise<void> {
  try {
    // Buscar todos os projetos para fazer o mapeamento de ID -> Nome
    const projectsMap = new Map<string, string>();
    try {
      const { data: projects, error } = await supabase
        .from('projects')
        .select('id, name')
        .eq('company_id', Array.isArray(companyId) ? companyId[0] : companyId);

      if (!error && projects) {
        projects.forEach(p => projectsMap.set(p.id, p.name));
      }
    } catch (error) {
      console.warn('[Export] Erro ao buscar projetos:', error);
    }

    // Prepare transactions data - ALL FIELDS (with original values for reimport)
    const transactionsData = transactions.map(t => ({
      // IDs e identificação
      'ID': t.id,
      'User ID': t.user_id,
      'Empresa ID': t.company_id || '',
      
      // Informações básicas
      'Descrição': t.description,
      'Observações': t.notes || '',
      'Valor': t.amount,
      'Valor Formatado': formatCurrency(t.amount),
      'Moeda': t.currency || 'BRL',
      'Tipo': translateEntryType(t.entry_type),
      'Tipo (Original)': t.entry_type, // Para reimportação
      
      // Categorização completa
      'Categoria Principal': t.main_category || '',
      'Subcategoria': t.subcategory || '',
      'Classificação': t.classification || '',
      'Categoria Orçamentária': t.budget_category || '',
      'Centro de Custo': t.cost_center || '',
      'Projeto': t.project_id ? (projectsMap.get(t.project_id) || t.project_id) : '',
      'Projeto ID': t.project_id || '',
      
      // Datas
      'Data Lançamento': formatDate(t.entry_date),
      'Data Lançamento (ISO)': t.entry_date || '',
      'Data Vencimento': formatDate(t.due_date),
      'Data Vencimento (ISO)': t.due_date || '',
      'Data Pagamento': formatDate(t.payment_date),
      'Data Pagamento (ISO)': t.payment_date || '',
      
      // Recorrência
      'Recorrente': t.is_recurring ? 'Sim' : 'Não',
      'Frequência': t.frequency || '',
      'Data Fim Recorrência': formatDate(t.recurrence_end_date),
      'Data Fim Recorrência (ISO)': t.recurrence_end_date || '',
      'Grupo Recorrência': t.recurrence_group_id || '',
      
      // Pagamento
      'Status Pagamento': translatePaymentStatus(t.payment_status),
      'Status Pagamento (Original)': t.payment_status, // Para reimportação
      'Método Pagamento': t.payment_method || '',
      'Nome Conta': t.account_name || '',
      'Detalhes Pagamento': t.payment_details || '',
      
      // Informações adicionais
      'Nº Documento': t.invoice_number || '',
      'Categoria Fiscal': t.tax_category || '',
      'Parceiro': t.business_partner || '',
      'Localização': t.location || '',
      'Stakeholders': t.stakeholders || '',
      'Fonte Documento': t.document_source || '',
      'Tags': t.tags?.join(', ') || '',
      
      // Controle
      'Simulação': t.is_simulation ? 'Sim' : 'Não',
      'Criado em': formatDate(t.created_at),
      'Criado em (ISO)': t.created_at,
      'Atualizado em': formatDate(t.updated_at),
      'Atualizado em (ISO)': t.updated_at,
    }));

    // Create workbook
    const wb = XLSX.utils.book_new();

    // Add transactions sheet
    const wsTransactions = XLSX.utils.json_to_sheet(transactionsData);
    
    // Set column widths - ALL FIELDS
    const columnWidths = [
      // IDs
      { wch: 10 }, // ID
      { wch: 15 }, // User ID
      { wch: 15 }, // Empresa ID
      
      // Básicas
      { wch: 35 }, // Descrição
      { wch: 35 }, // Observações
      { wch: 12 }, // Valor
      { wch: 15 }, // Valor Formatado
      { wch: 8 },  // Moeda
      { wch: 13 }, // Tipo
      { wch: 10 }, // Tipo (Original)
      
      // Categorização
      { wch: 20 }, // Categoria Principal
      { wch: 20 }, // Subcategoria
      { wch: 12 }, // Classificação
      { wch: 25 }, // Categoria Orçamentária
      { wch: 18 }, // Centro de Custo
      { wch: 25 }, // Projeto
      { wch: 15 }, // Projeto ID
      
      // Datas
      { wch: 15 }, // Data Lançamento
      { wch: 12 }, // Data Lançamento (ISO)
      { wch: 15 }, // Data Vencimento
      { wch: 12 }, // Data Vencimento (ISO)
      { wch: 15 }, // Data Pagamento
      { wch: 12 }, // Data Pagamento (ISO)
      
      // Recorrência
      { wch: 10 }, // Recorrente
      { wch: 12 }, // Frequência
      { wch: 18 }, // Data Fim Recorrência
      { wch: 12 }, // Data Fim Recorrência (ISO)
      { wch: 18 }, // Grupo Recorrência
      
      // Pagamento
      { wch: 15 }, // Status Pagamento
      { wch: 12 }, // Status Pagamento (Original)
      { wch: 18 }, // Método Pagamento
      { wch: 15 }, // Nome Conta
      { wch: 20 }, // Detalhes Pagamento
      
      // Adicionais
      { wch: 15 }, // Nº Documento
      { wch: 15 }, // Categoria Fiscal
      { wch: 25 }, // Parceiro
      { wch: 15 }, // Localização
      { wch: 20 }, // Stakeholders
      { wch: 18 }, // Fonte Documento
      { wch: 30 }, // Tags
      
      // Controle
      { wch: 10 }, // Simulação
      { wch: 15 }, // Criado em
      { wch: 12 }, // Criado em (ISO)
      { wch: 15 }, // Atualizado em
      { wch: 12 }, // Atualizado em (ISO)
    ];
    wsTransactions['!cols'] = columnWidths;

    XLSX.utils.book_append_sheet(wb, wsTransactions, 'Lançamentos');

    // Add master data if requested
    if (options.includeMasterData) {
      const masterData = await fetchMasterData(companyId);

      // Categories sheet
      if (masterData.categories.length > 0) {
        const categoriesData = masterData.categories.map(c => ({
          'Nome': c.name,
          'Tipo': c.type || '',
          'Ativa': c.is_active ? 'Sim' : 'Não',
        }));
        const wsCategories = XLSX.utils.json_to_sheet(categoriesData);
        XLSX.utils.book_append_sheet(wb, wsCategories, 'Categorias');
      }

      // Payment methods sheet
      if (masterData.paymentMethods.length > 0) {
        const methodsData = masterData.paymentMethods.map(m => ({
          'Nome': m.name,
          'Tipo': m.type || '',
          'Ativo': m.is_active ? 'Sim' : 'Não',
        }));
        const wsMethods = XLSX.utils.json_to_sheet(methodsData);
        XLSX.utils.book_append_sheet(wb, wsMethods, 'Métodos Pagamento');
      }

      // Business partners sheet
      if (masterData.businessPartners.length > 0) {
        const partnersData = masterData.businessPartners.map(p => ({
          'Nome': p.name,
          'Tipo': p.type || '',
          'Documento': p.document || '',
          'Ativo': p.is_active ? 'Sim' : 'Não',
        }));
        const wsPartners = XLSX.utils.json_to_sheet(partnersData);
        XLSX.utils.book_append_sheet(wb, wsPartners, 'Parceiros');
      }

      // ===== NOVAS ABAS SOLICITADAS =====

      // Categorias DFC (Budget Categories) sheet
      const budgetCategoriesData = [
        { 'Categoria DFC': 'Receitas Operacionais', 'Tipo': 'Receita', 'Atividade': 'Operacional' },
        { 'Categoria DFC': 'Custos dos Produtos / Serviços', 'Tipo': 'Despesa', 'Atividade': 'Operacional' },
        { 'Categoria DFC': 'OPEX (Despesas Operacionais)', 'Tipo': 'Despesa', 'Atividade': 'Operacional' },
        { 'Categoria DFC': 'Impostos pagos', 'Tipo': 'Despesa', 'Atividade': 'Operacional' },
        { 'Categoria DFC': 'CAPEX / Compra de Ativos', 'Tipo': 'Despesa', 'Atividade': 'Investimento' },
        { 'Categoria DFC': 'Venda de Ativos', 'Tipo': 'Receita', 'Atividade': 'Investimento' },
        { 'Categoria DFC': 'Outras Receitas não operacionais', 'Tipo': 'Receita', 'Atividade': 'Operacional' },
        { 'Categoria DFC': 'Capital / Aportes de Sócios', 'Tipo': 'Receita', 'Atividade': 'Financiamento' },
        { 'Categoria DFC': 'Empréstimos recebidos', 'Tipo': 'Receita', 'Atividade': 'Financiamento' },
        { 'Categoria DFC': 'Amortização de empréstimos', 'Tipo': 'Despesa', 'Atividade': 'Financiamento' },
        { 'Categoria DFC': 'Distribuição de Lucros / Dividendos / JCP', 'Tipo': 'Despesa', 'Atividade': 'Financiamento' },
        { 'Categoria DFC': 'Adiantamentos a Sócios', 'Tipo': 'Despesa', 'Atividade': 'Financiamento' },
        { 'Categoria DFC': 'Devolução de Adiantamentos', 'Tipo': 'Receita', 'Atividade': 'Financiamento' },
      ];
      const wsBudgetCategories = XLSX.utils.json_to_sheet(budgetCategoriesData);
      wsBudgetCategories['!cols'] = [
        { wch: 45 }, // Categoria DFC
        { wch: 12 }, // Tipo
        { wch: 18 }, // Atividade
      ];
      XLSX.utils.book_append_sheet(wb, wsBudgetCategories, 'Categorias DFC');

      // Classificações sheet
      const classificationsData = [
        { 'Classificação': 'Fixo', 'Descrição': 'Despesas fixas que ocorrem regularmente (ex: aluguel, salários)' },
        { 'Classificação': 'Variável', 'Descrição': 'Despesas que variam conforme o volume de atividade' },
        { 'Classificação': 'Eventual', 'Descrição': 'Despesas esporádicas ou não recorrentes' },
      ];
      const wsClassifications = XLSX.utils.json_to_sheet(classificationsData);
      wsClassifications['!cols'] = [
        { wch: 15 }, // Classificação
        { wch: 60 }, // Descrição
      ];
      XLSX.utils.book_append_sheet(wb, wsClassifications, 'Classificações');

      // Subcategories sheet - SEMPRE APARECE
      const subcategoriesData = masterData.subcategories.length > 0
        ? masterData.subcategories.map(s => ({
            'Nome': s.name,
            'Categoria Principal': s.main_category || '',
            'Descrição': s.description || '',
          }))
        : [{ 'Nome': '', 'Categoria Principal': '', 'Descrição': 'Nenhuma subcategoria cadastrada. Cadastre em Configurações.' }];
      
      const wsSubcategories = XLSX.utils.json_to_sheet(subcategoriesData);
      wsSubcategories['!cols'] = [
        { wch: 30 }, // Nome
        { wch: 25 }, // Categoria Principal
        { wch: 40 }, // Descrição
      ];
      XLSX.utils.book_append_sheet(wb, wsSubcategories, 'Subcategorias');

      // Projects sheet - SEMPRE APARECE
      const projectsData = masterData.projects.length > 0
        ? masterData.projects.map(p => ({
            'Nome': p.name,
            'Descrição': p.description || '',
            'Data Início': formatDate(p.start_date),
            'Data Fim': formatDate(p.end_date),
            'Orçamento': p.budget ? formatCurrency(p.budget) : '',
            'Status': translateProjectStatus(p.status),
          }))
        : [{ 'Nome': '', 'Descrição': 'Nenhum projeto cadastrado. Cadastre em Configurações.', 'Data Início': '', 'Data Fim': '', 'Orçamento': '', 'Status': '' }];
      
      const wsProjects = XLSX.utils.json_to_sheet(projectsData);
      wsProjects['!cols'] = [
        { wch: 30 }, // Nome
        { wch: 40 }, // Descrição
        { wch: 12 }, // Data Início
        { wch: 12 }, // Data Fim
        { wch: 15 }, // Orçamento
        { wch: 12 }, // Status
      ];
      XLSX.utils.book_append_sheet(wb, wsProjects, 'Projetos');
    }

    // Add instructions sheet
    const instructions = [
      ['COMO USAR ESTE ARQUIVO'],
      [''],
      ['Este arquivo contém os lançamentos financeiros exportados do sistema.'],
      [''],
      ['ESTRUTURA:'],
      ['• Aba "Lançamentos": Todos os lançamentos do período selecionado'],
      options.includeMasterData ? ['• Abas de dados mestres: Categorias, Métodos de Pagamento, Parceiros'] : [],
      options.includeMasterData ? ['• Abas de referência: Categorias DFC, Classificações, Subcategorias, Projetos'] : [],
      [''],
      ['ABAS DE REFERÊNCIA (para consulta e cópia):'],
      ['• Categorias DFC: 13 categorias de fluxo de caixa para classificação contábil'],
      ['• Classificações: Fixo, Variável ou Eventual'],
      ['• Subcategorias: Subcategorias cadastradas no sistema'],
      ['• Projetos: Projetos cadastrados no sistema'],
      [''],
      ['PARA IMPORTAR DE VOLTA:'],
      ['1. Não altere os headers (primeira linha)'],
      ['2. Mantenha os IDs para preservar vínculos'],
      ['3. Use valores permitidos nas colunas:'],
      ['   - Tipo: Receita, Despesa ou Transferência'],
      ['   - Status: Pago, Pendente, Vencido, Previsão, Confirmado'],
      ['   - Recorrente: Sim ou Não'],
      ['   - Frequência: daily, weekly, monthly, quarterly, yearly'],
      ['   - Classificação: Fixo, Variável ou Eventual'],
      [''],
      ['OBSERVAÇÕES:'],
      ['• Valores devem ser numéricos'],
      ['• Datas no formato DD/MM/AAAA'],
      ['• Para criar novos lançamentos, deixe o ID vazio'],
      ['• Lançamentos recorrentes mantêm o mesmo Grupo Recorrência'],
      ['• Use as abas de referência para copiar valores corretos'],
    ].filter(row => row.length > 0);

    const wsInstructions = XLSX.utils.aoa_to_sheet(instructions);
    XLSX.utils.book_append_sheet(wb, wsInstructions, 'Instruções');

    // Generate filename
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `lancamentos-${timestamp}.xlsx`;

    // Download
    XLSX.writeFile(wb, filename);
  } catch (error) {
    console.error('[Export Excel Service] Error:', error);
    throw error;
  }
}

